import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { QUERY_STATUS } from "constants/constants";
import { canUserPerformAction } from "access-roles/general-functions";
import { FAF_QUALIF_ROLES } from "access-roles/roles";
import { useUserContext } from "api/user/UserProvider";
import { toNewQualifPayload } from "utils/fafHelper";
import {
  createQualifQuery,
  deleteQualifQuery,
  getQualifReviewsQuery,
  getQualifsByDeclarationQuery,
  patchQualifReviewQuery,
} from "./queries";

const QualifContext = createContext({});

export const useQualifContext = () => useContext(QualifContext);

// Candidate qualifications — mission service. `qualifs` (GET /qualif) feeds the recap
// counter; `qualifReviews` (GET /qualif-review/<decl>) is the to-rule list of
// the qualifs step, each answer being saved on the fly (PATCH). Creation via POST.
export const QualifProvider = ({ ...props }) => {
  const { userRoleList } = useUserContext();

  const [qualifs, setQualifs] = useState([]);
  // [{ qualif, status, qualification_date }]
  const [qualifReviews, setQualifReviews] = useState([]);
  const [loadStatus, setLoadStatus] = useState(QUERY_STATUS.INITIAL);
  const loadedRef = useRef(null); // declaration already loaded for the recap counter
  const reviewsDeclarationRef = useRef(null); // keyed on the already-loaded declaration

  // Qualifs of the declaration (recap counter, declaration_ids required).
  const getQualifs = useCallback(
    async (declarationId) => {
      if (!declarationId || loadedRef.current === declarationId) return;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_QUALIF_ROLES.ROLE_GET_QUALIF,
        })
      )
        return;
      loadedRef.current = declarationId;
      setLoadStatus(QUERY_STATUS.LOADING);
      try {
        setQualifs((await getQualifsByDeclarationQuery(declarationId)) || []);
        setLoadStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        loadedRef.current = null;
        console.error("getQualifs", e);
        setLoadStatus(QUERY_STATUS.ERROR);
      }
    },
    [userRoleList]
  );

  // Qualifs attached to a declaration (edit list "being added"):
  // plain read that returns the list (the wizard rebuilds it into forms). Network
  // errors are propagated (the caller distinguishes "empty" from "failure" to retry).
  const getQualifsByDeclaration = useCallback(
    async (declarationId) => {
      if (
        !declarationId ||
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_QUALIF_ROLES.ROLE_GET_QUALIF,
        })
      )
        return [];
      return (await getQualifsByDeclarationQuery(declarationId)) || [];
    },
    [userRoleList]
  );

  // Qualif reviews of a declaration, returned WITHOUT touching the state (no ref-guard):
  // used for step inference on resume (have we already ruled on any pending qualifs?).
  // Errors propagated (the caller distinguishes "empty" from "failure"). Missing role → [].
  const getQualifReviewsByDeclaration = useCallback(
    async (declarationId) => {
      if (
        !declarationId ||
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_QUALIF_ROLES.ROLE_GET_QUALIF,
        })
      )
        return [];
      return (await getQualifReviewsQuery(declarationId)) || [];
    },
    [userRoleList]
  );

  // List of qualifs to review for the current declaration (reloaded if it changes).
  const getQualifReviews = useCallback(
    async (declarationId) => {
      if (!declarationId || reviewsDeclarationRef.current === declarationId) return;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_QUALIF_ROLES.ROLE_GET_QUALIF,
        })
      )
        return;
      reviewsDeclarationRef.current = declarationId;
      try {
        setQualifReviews((await getQualifReviewsQuery(declarationId)) || []);
      } catch (e) {
        reviewsDeclarationRef.current = null;
        console.error("getQualifReviews", e);
      }
    },
    [userRoleList]
  );

  // Immediate save (on click) of a review answer (PATCH upsert):
  // { qualif_id, status, qualification_date } — date required if status = Q2.
  const saveQualifReview = useCallback(
    async (declarationId, payload) => {
      if (!declarationId) return;
      // Mutation guard: fail loudly (see DeclarationProvider.createDeclaration). The caller
      // (setQualifStatus) rolls back the optimistic status and surfaces the error on rejection.
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_QUALIF_ROLES.ROLE_UPDATE_QUALIF,
        })
      )
        throw new Error("faf_missing_role");
      await patchQualifReviewQuery({ declarationId, payload });
    },
    [userRoleList]
  );

  // Synchronizes the step's new qualifs (create/delete) against the last snapshot,
  // so that a re-commit (e.g. retry after a submit failure) does not recreate a duplicate:
  //  - qualif without id → POST (we grab the returned id and reinject it);
  //  - qualif with id    → kept as-is (the back does NOT expose an update of a qualif's
  //    candidate fields: only the review status is modifiable, via qualif-review);
  // - qualif present in the snapshot but removed from the list → DELETE.
  // Returns the reconciled list (ids filled in): the caller keeps it as the snapshot.
  // DELETE guarded by UPDATE_QUALIF (the route requires it on the back side). Invalidates the list (recap counter).
  const syncQualifs = useCallback(
    async ({ declarationId, qualifs, loaded }) => {
      const can = (role) =>
        canUserPerformAction({ userRoles: userRoleList, requiredRoles: role });
      // Mutation guard fails loudly (see DeclarationProvider.createDeclaration): throw before
      // touching the server if the diff needs a role the user lacks, rather than silently
      // no-oping (which would leave created qualifs id-less and the step falsely "done").
      const requireRole = (role) => {
        if (!can(role)) throw new Error("faf_missing_role");
      };
      const loadedList = loaded || [];
      const currentList = qualifs || [];
      const keptIds = currentList.filter((q) => q.id).map((q) => q.id);

      const toDelete = loadedList.filter((l) => l.id && !keptIds.includes(l.id));
      const toCreate = currentList.filter((q) => !q.id);
      // DELETE route requires UPDATE_QUALIF on the back side (no qualif update route otherwise).
      if (toDelete.length) requireRole(FAF_QUALIF_ROLES.ROLE_UPDATE_QUALIF);
      if (toCreate.length) requireRole(FAF_QUALIF_ROLES.ROLE_CREATE_QUALIF);

      await Promise.all(toDelete.map((l) => deleteQualifQuery({ id: l.id })));

      const synced = await Promise.all(
        currentList.map(async (q) => {
          if (q.id) return q; // no qualif update route → keep as-is
          const created = await createQualifQuery({
            payload: toNewQualifPayload(q, declarationId),
          });
          return created?.id ? { ...q, id: created.id } : q;
        })
      );
      loadedRef.current = null; // recap counter to reload (new qualifs created)
      return synced;
    },
    [userRoleList]
  );

  const values = useMemo(
    () => ({
      qualifs,
      qualifReviews,
      loadStatus,
      getQualifs,
      getQualifsByDeclaration,
      getQualifReviews,
      getQualifReviewsByDeclaration,
      saveQualifReview,
      syncQualifs,
    }),
    [
      qualifs,
      qualifReviews,
      loadStatus,
      getQualifs,
      getQualifsByDeclaration,
      getQualifReviews,
      getQualifReviewsByDeclaration,
      saveQualifReview,
      syncQualifs,
    ]
  );

  return <QualifContext.Provider value={values} {...props} />;
};
